import {
  AxiosApiClient,
  HttpApiConfig,
  generateGUID,
  type HttpApiClient,
} from "@vana/core";
import { PostRepo } from "@vana/home";
import axios from "axios";
import { AllDeliveriesRepo } from "../features/all-deliveries/persistance/repositories/all-deliveries-repo";
import { BatchesRepo } from "../features/batches/persistance/repositories/batches-repo";
import { ComplianceRepo } from "../features/compliance/persistance/repositories/compliance-repo";
import { DayCloseRepo } from "../features/day-close/persistance/repositories/day-close-repo";
import { DeliveryRepo } from "../features/deliveries/persistance/repositories/delivery-repo";
import { EducationRepo } from "../features/education/persistance/repositories/education-repo";
import { FailedDeliveryRepo } from "../features/failed-delivery/persistance/repositories/failed-delivery-repo";
import { FirstPurchaseRepo } from "../features/first-purchase/persistance/repositories/first-purchase-repo";
import { KycRepo } from "../features/kyc/persistance/repositories/kyc-repo";
import { ProblemReportRepo } from "../features/problem-report/persistance/repositories/problem-report-repo";
import { ReferralsBackofficeRepo } from "../features/referrals-backoffice/persistance/repositories/referrals-backoffice-repo";
import { ReferralsRepo } from "../features/referrals/persistance/repositories/referrals-repo";
import { ReportsRepo } from "../features/reports/persistance/repositories/reports-repo";
import { SummaryRepo } from "../features/summary/persistance/repositories/summary-repo";
import { TeamRepo } from "../features/team/persistance/repositories/team-repo";
import { ReschedulesRepo } from "../features/reschedules/persistance/repositories/reschedules-repo";
import { UndeliveredRepo } from "../features/undelivered/persistance/repositories/undelivered-repo";
import { ContactCenterRepo } from "../features/pending-confirmation/persistance/repositories/contact-center-repo";
import { SupervisorDeliveryRepo } from "../features/supervisor-delivery/persistance/repositories/supervisor-delivery-repo";
import { RouteAssignmentRepo } from "../features/route-assignment/persistance/repositories/route-assignment-repo";
import { UsersRepo } from "../features/users/persistance/repositories/users-repo";
import { ZonesRepo } from "../features/zones/persistance/repositories/zones-repo";
import { redirectIfUnauthorized } from "../auth/session-expired";
import { build, country, locale } from "../infrastructure/build";
import { type ExtraArgument } from "./extra-argument";

const apiAxios = axios.create({
  baseURL: build.apiHost,
  timeout: HttpApiConfig.Timeout,
});

// A 401 on an API call means the BFF session is gone (expired or revoked). Send
// the user to re-login instead of surfacing a cryptic error; the full-page
// navigation re-initialises the auth context. Auth endpoints use `fetch` (not
// this axios instance), so this only ever fires for `/api` and can't loop with
// the login flow. The raw `fetch`/`<img>` call sites that bypass axios (binary
// downloads, thumbnails) use the same helper — see auth/session-expired.ts.
apiAxios.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (typeof status === "number") redirectIfUnauthorized(status);
    return Promise.reject(error);
  },
);

const httpApiClient: HttpApiClient = new AxiosApiClient(apiAxios, () => ({
  "X-Request-ID": generateGUID(),
}));

const postRepo = new PostRepo(httpApiClient);
const deliveryRepo = new DeliveryRepo(httpApiClient);
const dayCloseRepo = new DayCloseRepo(httpApiClient);
const kycRepo = new KycRepo(httpApiClient);
const educationRepo = new EducationRepo(httpApiClient);
const firstPurchaseRepo = new FirstPurchaseRepo(httpApiClient);
const referralsRepo = new ReferralsRepo(httpApiClient);
const referralsBackofficeRepo = new ReferralsBackofficeRepo(httpApiClient);
const failedDeliveryRepo = new FailedDeliveryRepo(httpApiClient);
const problemReportRepo = new ProblemReportRepo(httpApiClient);
const routeAssignmentRepo = new RouteAssignmentRepo(httpApiClient);
const usersRepo = new UsersRepo(httpApiClient);
const zonesRepo = new ZonesRepo(httpApiClient);
const reportsRepo = new ReportsRepo(httpApiClient);
const summaryRepo = new SummaryRepo(httpApiClient);
const teamRepo = new TeamRepo(httpApiClient);
const supervisorDeliveryRepo = new SupervisorDeliveryRepo(httpApiClient);
const reschedulesRepo = new ReschedulesRepo(httpApiClient);
const contactCenterRepo = new ContactCenterRepo(httpApiClient);
const undeliveredRepo = new UndeliveredRepo(httpApiClient);
const batchesRepo = new BatchesRepo(httpApiClient);
const allDeliveriesRepo = new AllDeliveriesRepo(httpApiClient);
const complianceRepo = new ComplianceRepo(httpApiClient);

export const extraArgument: ExtraArgument = {
  build,
  country,
  locale,
  postRepo,
  deliveryRepo,
  dayCloseRepo,
  kycRepo,
  educationRepo,
  firstPurchaseRepo,
  referralsRepo,
  referralsBackofficeRepo,
  failedDeliveryRepo,
  problemReportRepo,
  routeAssignmentRepo,
  usersRepo,
  zonesRepo,
  reportsRepo,
  summaryRepo,
  teamRepo,
  supervisorDeliveryRepo,
  reschedulesRepo,
  contactCenterRepo,
  undeliveredRepo,
  batchesRepo,
  allDeliveriesRepo,
  complianceRepo,
};
